import { graphlib, layout as dagreLayout } from '@dagrejs/dagre'
import { relationFamily } from './families.js'
import type { ChapterGraph } from './types.js'

export interface NodeBox {
  /** Centro del rettangolo, coordinate di layout (non viewport). */
  x: number
  y: number
  w: number
  h: number
}

export interface ChapterLayout {
  nodes: Record<string, NodeBox>
  width: number
  height: number
}

/**
 * Stima delle dimensioni del nodo dai soli caratteri dell'etichetta: nessuna
 * misura del DOM (deterministico, portabile su React Native).
 */
export function estimateNodeSize(label: string, withCaption = false): { w: number; h: number } {
  const w = label.length > 34 ? 176 : label.length > 18 ? 156 : 132
  const lines = Math.ceil(label.length / (w / 7.6))
  // 16 = padding verticale del box, 18 = interlinea reale a 15px/1.2,
  // 16 = riga di didascalia (titolo del capitolo) sui nodi esterni.
  const h = Math.max(44, 16 + 18 * lines + (withCaption ? 16 : 0))
  return { w, h }
}

/**
 * Layout a strati (dagre) del capitolo: solo archi intra residenti con
 * famiglia `structure` o `dynamic` (esclusi `opposes` e i tipi sconosciuti).
 * Deterministico: input ordinato per nodeId/edgeId, nessun `Math.random`.
 */
export function layoutChapter(graph: ChapterGraph): ChapterLayout {
  const g = new graphlib.Graph()
  g.setGraph({
    rankdir: 'TB',
    nodesep: 26,
    ranksep: 80,
    marginx: 8,
    marginy: 8,
    ranker: 'network-simplex',
  })
  g.setDefaultEdgeLabel(() => ({}))

  const nodeIds = new Set(graph.nodes.map((n) => n.nodeId))
  const sortedNodes = [...graph.nodes].sort((a, b) =>
    a.nodeId < b.nodeId ? -1 : a.nodeId > b.nodeId ? 1 : 0,
  )
  for (const n of sortedNodes) {
    const size = estimateNodeSize(n.label)
    g.setNode(n.nodeId, { width: size.w, height: size.h })
  }

  const layoutEdges = graph.edges.filter(
    (e) => e.scope === 'intra' && nodeIds.has(e.sourceNodeId) && nodeIds.has(e.targetNodeId),
  )
  const sortedEdges = [...layoutEdges].sort((a, b) =>
    a.edgeId < b.edgeId ? -1 : a.edgeId > b.edgeId ? 1 : 0,
  )
  for (const e of sortedEdges) {
    const family = relationFamily(e.relationType)
    if (family !== 'structure' && family !== 'dynamic') continue
    if (family === 'structure') {
      // Il concetto più generale sta sopra: arco invertito (target → source).
      g.setEdge(e.targetNodeId, e.sourceNodeId, { id: e.edgeId })
    } else {
      g.setEdge(e.sourceNodeId, e.targetNodeId, { id: e.edgeId })
    }
  }

  dagreLayout(g)

  const graphLabel = g.graph() as { width?: number; height?: number }
  const nodes: Record<string, NodeBox> = {}
  // Solo i nodi reali del capitolo: dagre può inserire nodi "dummy" per gli
  // archi che attraversano più strati, da escludere dall'output.
  for (const n of sortedNodes) {
    const box = g.node(n.nodeId) as { x: number; y: number; width: number; height: number }
    nodes[n.nodeId] = { x: box.x, y: box.y, w: box.width, h: box.height }
  }

  return { nodes, width: graphLabel.width ?? 0, height: graphLabel.height ?? 0 }
}
